<template>
  <form
      @submit.prevent="submitReturn"
      autocomplete="off"
      class="w-full max-h-screen bg-background flex flex-col relative"
      :class="{ 'overflow-y-hidden': isBusy }"
  >
    <div v-show="isBusy" class="absolute inset-0 bg-faint-white flex items-center justify-center z-10">
      <spinner :color="'text-black'" :size="10" />
    </div>

    <page-header :title="pageTitle" :info="ret.return_no" back-to="returns">
      <button
          v-if="returnId"
          @click="showReturnLabel"
          type="button"
          data-testid="return-label-button"
          class="mr-4 flex items-center bg-green-200 border hover:bg-green-300 border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z" />
          <path d="M7 7h.01" />
        </svg>
        <span>Return Label</span>
      </button>
      <button
          @click="saveReturn"
          type="button"
          class="flex items-center bg-blue-200 border hover:bg-blue-300 border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-300 text-secondary rounded-md px-2 py-1"
      >
        <svg
            class="stroke-current h-5 w-5 mr-2"
            fill="none"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            viewBox="0 0 24 24"
        >
          <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
          <path d="M17 21v-8H7v8M7 3v5h8" />
        </svg>
        <span>Save</span>
      </button>
    </page-header>

    <main class="p-6 bg-background flex-1 overflow-y-scroll">
      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Return Details</h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>

          <div class="flex flex-wrap -my-3 -mx-5">
            <div class="w-full flex flex-col mt-3">
              <div class="flex flex-wrap">
                <div class="w-full sm:w-1/2 lg:w-1/3 flex flex-col px-5 mb-3">
                  <label class="uppercase font-semibold text-sm">REQUESTED BY <span class="text-red-400">*</span></label>
                  <StaffAutocomplete v-model="ret.requested_by" />
                </div>
                <div class="w-full sm:w-1/2 lg:w-1/3 flex flex-col px-5 mb-3">
                  <label class="uppercase font-semibold text-sm">AGENT</label>
                  <ContactAutocomplete v-model="ret.agent" :is_agent="true" />
                </div>
                <div class="w-full sm:w-1/2 lg:w-1/3 flex flex-col px-5 mb-3">
                  <label class="uppercase font-semibold text-sm">CUSTOMER / OWNER</label>
                  <ContactAutocomplete v-model="ret.customer" :is_customer="true" />
                </div>
              </div>
            </div>

            <div class="flex flex-col w-full sm:w-1/2 lg:w-1/3 my-3 px-5">
              <label class="uppercase font-semibold text-sm">INTENDED RECIPIENT / FAO</label>
              <AutoComplete
                  v-model="recipientModel"
                  :suggestions="recipientSuggestions"
                  @complete="searchRecipients"
                  :forceSelection="false"
                  field="name"
                  inputClass="bge-input bge-input-spacing rounded w-full"
                  class="w-full"
              />
            </div>

            <div class="flex flex-col w-full sm:w-1/2 lg:w-1/3 my-3 px-5">
              <label class="uppercase font-semibold text-sm">PRODUCT CODE</label>
              <ProductAutoComplete
                  v-model="ret.product"
                  @productSelected="onProductSelected"
                  @productCleared="onProductCleared"
              />
            </div>

            <div class="flex flex-col w-full sm:w-1/2 lg:w-1/3 my-3 px-5">
              <label class="uppercase font-semibold text-sm">ITEM DESCRIPTION</label>
              <input v-model="ret.product_name" class="bge-input bge-input-spacing rounded" type="text" />
            </div>

            <div class="flex flex-col w-full sm:w-1/2 lg:w-1/3 my-3 px-5">
              <label class="uppercase font-semibold text-sm">ITEM SERIAL NUMBER</label>
              <input v-model="ret.item_serial_number" class="bge-input bge-input-spacing rounded" type="text" />
            </div>

            <div class="flex flex-col w-full sm:w-1/2 lg:w-1/3 my-3 px-5">
              <label class="uppercase font-semibold text-sm">INVOICE / WARRANTY NUMBER</label>
              <input v-model="ret.invoice_warranty_number" class="bge-input bge-input-spacing rounded" type="text" />
            </div>

            <div class="flex flex-col w-full sm:w-1/2 lg:w-1/3 my-3 px-5">
              <div class="flex w-full">
                <div class="flex flex-col w-3/5 pr-2">
                  <label class="uppercase font-semibold text-sm">CONFIRMED BY</label>

                  <div :class="!canConfirm ? 'opacity-50 pointer-events-none' : ''">
                    <StaffAutocomplete v-model="ret.confirmed_by" />
                  </div>

                  <div v-if="!canConfirm" class="text-xs text-gray-600 mt-1">
                    Save the return to enable confirmation.
                  </div>
                </div>

                <div class="w-2/5 flex flex-col justify-center items-end">
                  <div :class="!canConfirm ? 'opacity-50 pointer-events-none' : ''">
                    <Toggle
                        class="mt-6 mr-2"
                        label="Confirmed"
                        :checked="ret.status === 'confirmed'"
                        @value-changed="onStatusToggle"
                        labelPosition="left"
                        labelClasses="uppercase font-semibold text-sm"
                    />
                  </div>

                  <div v-if="canConfirm && ret.status === 'confirmed' && ret.confirmed_at" class="text-xs text-gray-600 mt-2 mr-2">
                    Confirmed at: {{ formatUnix(ret.confirmed_at) }}
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </div>

      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Reason For Return</h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>
          <Editor editorStyle="height: 120px" v-model="ret.reason_for_return" :options="editorOptions">
            <template #toolbar>
              <span class="ql-formats">
                <button class="ql-bold"></button>
                <button class="ql-italic"></button>
                <button class="ql-underline"></button>
                <button class="ql-list" value="ordered"></button>
                <button class="ql-list" value="bullet"></button>
              </span>
            </template>
          </Editor>
        </div>
      </div>

      <div v-if="returnId" class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Manage Files</h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>
          <ManageFiles :uploadRefId="returnId" />
        </div>
      </div>
      <div v-else class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Manage Files</h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>

          <div class="text-sm text-gray-600">
            Save the return first to enable file upload.
          </div>
        </div>
      </div>


      <div class="w-full bg-white mt-6 p-5 pt-1 rounded shadow-md relative">
        <div class="mt-6">
          <div class="flex items-center mb-5">
            <h3 class="uppercase text-lg font-semibold text-primary tracking-wide">Notes</h3>
            <hr class="flex-1 border-primary ml-4" />
          </div>
          <Editor editorStyle="height: 120px" v-model="ret.notes" :options="editorOptions">
            <template #toolbar>
              <span class="ql-formats">
                <button class="ql-bold"></button>
                <button class="ql-italic"></button>
                <button class="ql-underline"></button>
                <button class="ql-list" value="ordered"></button>
                <button class="ql-list" value="bullet"></button>
              </span>
            </template>
          </Editor>
        </div>
      </div>

      <ReturnTimeline v-if="returnId" :returnNo="ret.return_no" />
    </main>

    <Dialog
        :showHeader="false"
        :visible.sync="labelDialogIsOpen"
        :modal="true"
        :containerStyle="{ width: '50vw' }"
    >
      <div class="flex flex-col py-6 px-4 h-auto w-full" v-if="label">
        <h2 class="text-secondary font-bold text-3xl mb-6 leading-none text-center">
          Return Label
        </h2>

        <ReturnLabel ref="returnLabel" :result="label" :compact="true" />

        <div class="w-full mt-6 text-left">
          <label class="uppercase font-semibold text-sm">Email this label to</label>
          <div class="flex mt-1">
            <Dropdown
                v-model="labelEmail"
                :options="labelRecipients"
                optionLabel="display"
                optionValue="email"
                :editable="true"
                placeholder="Choose an address or type one"
                class="w-full"
                data-testid="label-email"
            />
            <button
                type="button"
                @click="emailReturnLabel"
                :disabled="isSendingLabel"
                data-testid="send-return-label"
                class="ml-2 flex items-center whitespace-no-wrap bg-green-200 hover:bg-green-300 text-secondary py-2 px-4 font-semibold rounded border border-green-400 hover:border-green-500 hover:shadow active:shadow-inner transition duration-100"
            >
              <svg
                  class="stroke-current h-5 w-5 mr-2"
                  fill="none"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  viewBox="0 0 24 24"
              >
                <path d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z" />
              </svg>
              <span>Send</span>
            </button>
          </div>
          <p v-if="labelSentTo" class="text-sm text-green-700 mt-1" data-testid="label-sent-note">
            Label emailed to {{ labelSentTo }}
          </p>

          <label class="uppercase font-semibold text-sm block mt-4">Email History</label>
          <EmailQueueTable ref="labelEmailHistory" :referenceId="returnId" />
        </div>

        <div class="flex justify-center w-full mt-6 -mx-2">
          <button
              type="button"
              @click="printReturnLabel"
              data-testid="print-return-label"
              class="w-1/2 mx-2 flex items-center justify-center bg-gray-200 hover:bg-gray-300 text-secondary py-2 px-4 font-semibold rounded border border-gray-400 hover:border-gray-500 hover:shadow active:shadow-inner transition duration-100"
          >
            <svg
                class="stroke-current h-5 w-5 mr-2"
                fill="none"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                viewBox="0 0 24 24"
            >
              <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2" />
              <path d="M6 14h12v8H6z" />
            </svg>
            <span>Print Label</span>
          </button>
          <button
              type="button"
              @click="labelDialogIsOpen = false"
              class="w-1/2 mx-2 flex items-center justify-center bg-blue-200 hover:bg-blue-300 text-secondary py-2 px-4 font-semibold rounded border border-blue-400 hover:border-blue-500 hover:shadow active:shadow-inner transition duration-100"
          >
            <span>Done</span>
          </button>
        </div>
      </div>
    </Dialog>
  </form>
</template>

<script>
import PageHeader from "@/components/PageHeader.vue";
import Spinner from "@/components/Spinner.vue";
import ContactAutocomplete from "@/components/AutoComplete/ContactAutoComplete.vue";
import StaffAutocomplete from "@/components/AutoComplete/StaffAutoComplete.vue";
import ProductAutoComplete from "@/components/AutoComplete/ProductAutoComplete.vue";
import Toggle from "@/components/Toggle.vue";
import ManageFiles from "./Components/ManageFiles.vue";
import ReturnTimeline from "./Components/ReturnTimeline.vue";
import EmailQueueTable from "@/views/HR/Sales/Components/EmailQueueTable.vue";
import ReturnLabel from "@/views/Public/ReturnLabel.vue";
import notificationMixin from "@/mixins/notificationMixin";
import AutoComplete from "primevue/autocomplete";

const RECIPIENT_DEPARTMENTS = [
  { name: "REPAIR DEPARTMENT", staff_id: "dept_repair" },
  { name: "SERVICE DEPARTMENT", staff_id: "dept_service" },
  { name: "ADMIN DEPARTMENT", staff_id: "dept_admin" },
  { name: "OTHER", staff_id: "dept_other" },
];

export default {
  name: "ManageReturn",
  mixins: [notificationMixin],
  components: {
    PageHeader,
    Spinner,
    ContactAutocomplete,
    StaffAutocomplete,
    ProductAutoComplete,
    Toggle,
    ManageFiles,
    AutoComplete,
    ReturnLabel,
    ReturnTimeline,
    EmailQueueTable,
  },
  props: {
    returnId: String,
  },
  data() {
    return {
      isBusy: false,
      recipientSuggestions: [],
      label: null,
      labelDialogIsOpen: false,
      labelRecipients: [],
      labelEmail: "",
      labelSentTo: "",
      isSendingLabel: false,
      linkedTicketId: "",
      ret: {
        requested_by: null,
        agent: null,
        customer: null,
        recipient: null,
        product: "",
        product_name: "",
        item_serial_number: "",
        invoice_warranty_number: "",
        return_no: "",
        reason_for_return: "",
        notes: "",
        status: "open",
        confirmed_at: null,
        confirmed_by: null,
      },
      editorOptions: {
        modules: {
          toolbar: [
            ["bold", "italic", "underline"],
            [{ list: "ordered" }, { list: "bullet" }],
          ],
        },
        theme: "snow",
      },
    };
  },
  computed: {
    pageTitle() {
      return this.returnId ? "Update Return" : "Create Return";
    },
    recipientModel: {
      get() {
        return this.ret.recipient;
      },
      set(newValue) {
        if (typeof newValue === "string") {
          const v = this.toStr(newValue).trim();
          if (!v) {
            this.ret.recipient = null;
            return;
          }
          this.ret.recipient = { name: v, staff_id: null };
          return;
        }

        if (newValue && typeof newValue === "object") {
          const name = this.toStr(newValue.name).trim();
          const staffId = this.toStr(newValue.staff_id).trim();
          if (!name) {
            this.ret.recipient = null;
            return;
          }
          this.ret.recipient = { name: name, staff_id: staffId ? staffId : null };
          return;
        }

        this.ret.recipient = null;
      },
    },
    canConfirm() {
      return !!this.returnId;
    },
  },
  mounted() {
    if (this.returnId) {
      this.loadReturn();
    } else {
      if (this.$store && this.$store.state && this.$store.state.user && this.$store.state.user.staff_id) {
        this.ret.requested_by = {
          name: (this.$store.state.user.first_name || "") + " " + (this.$store.state.user.last_name || ""),
          staff_id: this.$store.state.user.staff_id,
        };
      }

      const ticketId = this.$route && this.$route.query ? this.toStr(this.$route.query.ticketId).trim() : "";
      if (ticketId) {
        this.linkedTicketId = ticketId;
        this.prefillFromTicket(ticketId);
      }
    }
  },
  methods: {
    toStr(v) {
      if (v === undefined || v === null) return "";
      return String(v);
    },
    uniqRecipients(list) {
      const map = {};
      const out = [];
      for (let i = 0; i < list.length; i++) {
        const it = list[i];
        if (!it) continue;
        const name = this.toStr(it.name).trim();
        const staffId = this.toStr(it.staff_id).trim();
        if (!name) continue;
        const key = staffId + "|" + name;
        if (!map[key]) {
          map[key] = true;
          out.push({ name: name, staff_id: staffId ? staffId : null });
        }
      }
      return out;
    },
    escapeHtml(value) {
      return this.toStr(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    },
    async prefillFromTicket(ticketId) {
      try {
        this.isBusy = true;
        const ticket = await this.ServiceTicketService.getServiceTicketById(ticketId);
        if (ticket) this.applyTicketPrefill(ticket);
      } catch (e) {
        this.notifyWarning("Could not load the service ticket details.");
      } finally {
        this.isBusy = false;
      }
    },
    applyTicketPrefill(ticket) {
      const equipment = ticket.equipment ? ticket.equipment : {};

      const contactId = this.toStr(ticket.contact_id).trim();
      const contactName = this.toStr(ticket.contact_name).trim();
      if (contactId) {
        this.ret.customer = { contact_id: contactId, name: contactName };
      } else if (contactName) {
        this.ret.customer = { contact_id: null, name: contactName };
      }

      const productId = this.toStr(equipment.product_id).trim();
      const productCode = this.toStr(equipment.product_code).trim();
      if (productId || productCode) {
        this.ret.product = { product_id: productId ? productId : null, name: productCode };
      }

      const productName = this.toStr(equipment.name).trim();
      this.ret.product_name = productName ? productName : this.toStr(ticket.equipment_name);
      this.ret.item_serial_number = this.toStr(equipment.serial_number);
      this.ret.invoice_warranty_number = this.toStr(ticket.warranty_number);
      this.ret.reason_for_return = this.ticketReasonHtml(ticket);
    },
    ticketReasonHtml(ticket) {
      const parts = [];

      const ticketNumber = this.toStr(ticket.ticket_number).trim();
      if (ticketNumber) parts.push("<p>Service Ticket " + this.escapeHtml(ticketNumber) + "</p>");

      const notes = this.toStr(ticket.items_for_return_notes).trim();
      if (notes) parts.push("<p>" + this.escapeHtml(notes) + "</p>");

      return parts.join("");
    },
    onProductSelected(event) {
      const item = event && event.value ? event.value : event;
      const productName = this.toStr(item && item.name).trim();
      const productId = this.toStr(item && item.product_id).trim();
      const productCode = this.toStr(item && item.product_code).trim();

      this.ret.product = {
        product_id: productId ? productId : null,
        name: productCode,
      };

      if (productName) this.ret.product_name = productName;
    },
    onProductCleared() {
      this.ret.product = "";
      this.ret.product_name = "";
    },
    onStatusToggle(value) {
      if (value) {
        this.ret.status = "confirmed";
        return;
      }
      this.ret.status = "open";
      this.ret.confirmed_at = null;
      this.ret.confirmed_by = null;
    },
    formatUnix(unix) {
      if (!unix) return "";
      return this.$moment.unix(unix).format("DD/MM/YYYY HH:mm");
    },
    async showReturnLabel() {
      try {
        this.isBusy = true;
        const result = await this.ReturnService.issueLabel(this.returnId);
        if (!result || !result.receipt_token) {
          this.notifyWarning("The return label came back without a scan code, please try again");
          return;
        }
        this.label = result;
        this.labelSentTo = "";
        this.labelDialogIsOpen = true;
        await this.loadLabelRecipients();
      } catch (error) {
        let message = "Could not make the return label";
        if (error && error.data && error.data.message) message = this.toStr(error.data.message);
        this.notifyWarning(message);
      } finally {
        this.isBusy = false;
      }
    },
    printReturnLabel() {
      if (this.$refs.returnLabel) this.$refs.returnLabel.printLabel();
    },
    async loadLabelRecipients() {
      try {
        const recipients = await this.ReturnService.getLabelRecipients(this.returnId);
        this.labelRecipients = recipients.map((r) => ({
          name: r.name,
          email: r.email,
          display: r.name ? `${r.name} <${r.email}> - ${r.source}` : `${r.email} - ${r.source}`,
        }));
      } catch (error) {
        this.labelRecipients = [];
      }
      if (!this.labelEmail && this.labelRecipients.length > 0) this.labelEmail = this.labelRecipients[0].email;
    },
    async emailReturnLabel() {
      const email = this.toStr(this.labelEmail).trim();
      if (!email) {
        this.notifyWarning("Please choose or type an email address");
        return;
      }

      const known = this.labelRecipients.find((r) => r.email === email.toLowerCase());
      try {
        this.isSendingLabel = true;
        const result = await this.ReturnService.sendLabel(this.returnId, {
          email: email,
          name: known ? known.name : "",
        });
        this.labelSentTo = result.sent_to;
        this.notifySuccess(`Return label emailed to ${result.sent_to}`);
        if (this.$refs.labelEmailHistory) await this.$refs.labelEmailHistory.loadEmailQueues();
      } catch (error) {
        let message = "Could not send the return label";
        if (error && error.data && error.data.message) message = this.toStr(error.data.message);
        this.notifyWarning(message);
      } finally {
        this.isSendingLabel = false;
      }
    },
    async loadReturn() {
      try {
        this.isBusy = true;
        const apiRet = await this.ReturnService.getReturnById(this.returnId);
        this.ret = this.mapApiToUi(apiRet);
      } finally {
        this.isBusy = false;
      }
    },
    submitReturn(event) {
      event.preventDefault();
    },
    async searchRecipients(event) {
      const q = this.toStr(event && event.query).trim().toLowerCase();

      const deptMatches = [];
      for (let i = 0; i < RECIPIENT_DEPARTMENTS.length; i++) {
        const d = RECIPIENT_DEPARTMENTS[i];
        const dn = this.toStr(d.name).toLowerCase();
        if (!q || dn.indexOf(q) >= 0) deptMatches.push({ name: d.name, staff_id: d.staff_id });
      }

      let staffMatches = [];
      try {
        if (this.StaffService && typeof this.StaffService.searchStaff === "function") {
          const res = await this.StaffService.searchStaff({
            searchTerm: this.toStr(event && event.query),
            limit: 50,
            page: 1,
          });

          const list = res && res.data ? res.data : Array.isArray(res) ? res : [];
          for (let i = 0; i < list.length; i++) {
            const s = list[i] || {};
            let name = "";
            if (s.name) name = this.toStr(s.name);
            else name = (this.toStr(s.first_name).trim() + " " + this.toStr(s.last_name).trim()).trim();

            let staffId = "";
            if (s.staff_id) staffId = this.toStr(s.staff_id);
            else if (s.id) staffId = this.toStr(s.id);
            else if (s.user_id) staffId = this.toStr(s.user_id);

            name = this.toStr(name).trim();
            staffId = this.toStr(staffId).trim();

            if (name) staffMatches.push({ name: name, staff_id: staffId ? staffId : null });
          }
        }
      } catch (e) {
        staffMatches = [];
      }

      const combined = staffMatches.concat(deptMatches);
      const unique = this.uniqRecipients(combined);

      const filtered = [];
      for (let i = 0; i < unique.length; i++) {
        const v = unique[i];
        const vn = this.toStr(v.name).toLowerCase();
        if (!q || vn.indexOf(q) >= 0) filtered.push(v);
      }

      this.recipientSuggestions = filtered.slice(0, 25);
    },
    validateReturn() {
      let requestedByName = "";
      if (this.ret.requested_by && typeof this.ret.requested_by === "object") requestedByName = this.toStr(this.ret.requested_by.name).trim();
      else requestedByName = this.toStr(this.ret.requested_by).trim();

      if (!requestedByName) {
        this.notifyWarning("Please select Requested By.");
        return false;
      }

      let agentId = "";
      let customerId = "";
      let customerName = "";
      if (this.ret.agent && typeof this.ret.agent === "object") agentId = this.toStr(this.ret.agent.contact_id).trim();
      if (this.ret.customer && typeof this.ret.customer === "object") {
        customerId = this.toStr(this.ret.customer.contact_id).trim();
        customerName = this.toStr(this.ret.customer.name).trim();
      }

      if (!agentId && !customerId && !customerName) {
        this.notifyWarning("Please select or enter Agent or Customer.");
        return false;
      }

      if (!this.ret.recipient) {
        this.notifyWarning("Please enter Intended Recipient / FAO.");
        return false;
      }

      if (typeof this.ret.recipient === "object") {
        const rn = this.toStr(this.ret.recipient.name).trim();
        if (!rn) {
          this.notifyWarning("Please enter Intended Recipient / FAO.");
          return false;
        }
      } else {
        const rn = this.toStr(this.ret.recipient).trim();
        if (!rn) {
          this.notifyWarning("Please enter Intended Recipient / FAO.");
          return false;
        }
      }


      if (this.ret.status === "confirmed") {
        let cn = "";
        if (this.ret.confirmed_by && typeof this.ret.confirmed_by === "object") {
          cn = this.toStr(this.ret.confirmed_by.name).trim();
        } else {
          cn = this.toStr(this.ret.confirmed_by).trim();
        }

        if (!cn) {
          this.notifyWarning("Please select Confirmed By.");
          return false;
        }
      }


      return true;
    },
    mapApiToUi(apiRet) {
      const r = apiRet || {};

      let requestedBy = null;
      if (r.requested_by) requestedBy = { staff_id: null, name: this.toStr(r.requested_by) };

      let agent = null;
      if (r.agent_contact && r.agent_contact.contact_id) agent = { contact_id: this.toStr(r.agent_contact.contact_id), name: this.toStr(r.agent_contact.name) };
      if (!agent && r.agent_id) agent = { contact_id: this.toStr(r.agent_id), name: "" };

      let customer = null;
      if (r.customer_contact && r.customer_contact.contact_id) customer = { contact_id: this.toStr(r.customer_contact.contact_id), name: this.toStr(r.customer_contact.name) };
      if (!customer && r.customer_id) customer = { contact_id: this.toStr(r.customer_id), name: "" };
      if (!customer && this.toStr(r.customer_name).trim()) customer = { contact_id: null, name: this.toStr(r.customer_name) };

      let recipient = null;
      if (r.recipient && typeof r.recipient === "object") {
        const rn = this.toStr(r.recipient.name).trim();
        const rs = this.toStr(r.recipient.staff_id).trim();
        if (rn) recipient = { name: rn, staff_id: rs ? rs : null };
      } else {
        const old = this.toStr(r.recipient).trim();
        if (old) recipient = { name: old, staff_id: null };
      }

      let product = "";
      if (r.product && r.product.product_id) {
        product = { product_id: this.toStr(r.product.product_id), name: this.toStr(r.product.product_code) };
      } else {
        const pid = this.toStr(r.product_id).trim();
        const pcode = this.toStr(r.product_code).trim();
        if (pid || pcode) product = { product_id: pid ? pid : null, name: pcode };
      }

      let confirmedBy = null;
      if (r.confirmed_by && typeof r.confirmed_by === "object") {
        const cn = this.toStr(r.confirmed_by.name).trim();
        const cs = this.toStr(r.confirmed_by.staff_id).trim();
        if (cn) confirmedBy = { name: cn, staff_id: cs ? cs : null };
      }

      return {
        requested_by: requestedBy,
        agent: agent,
        customer: customer,
        recipient: recipient,
        product: product,
        product_name: this.toStr(r.product && r.product.name ? r.product.name : r.product_name),
        item_serial_number: this.toStr(r.item_serial_number),
        invoice_warranty_number: this.toStr(r.invoice_warranty_number),
        return_no: this.toStr(r.return_no),
        reason_for_return: this.toStr(r.reason_for_return),
        notes: this.toStr(r.notes),
        status: this.toStr(r.status || "open"),
        confirmed_at: r.confirmed_at ? r.confirmed_at : null,
        confirmed_by: confirmedBy,
      };
    },
    mapUiToApiPayload() {
      let requestedBy = "";
      if (this.ret.requested_by && typeof this.ret.requested_by === "object") requestedBy = this.toStr(this.ret.requested_by.name);
      else requestedBy = this.toStr(this.ret.requested_by);

      let agentId = "";
      let customerId = "";
      let customerName = "";
      if (this.ret.agent && typeof this.ret.agent === "object") agentId = this.toStr(this.ret.agent.contact_id);
      if (this.ret.customer && typeof this.ret.customer === "object") {
        customerId = this.toStr(this.ret.customer.contact_id);
        if (!customerId) customerName = this.toStr(this.ret.customer.name).trim();
      }

      let recipient = null;
      if (this.ret.recipient && typeof this.ret.recipient === "object") {
        const rn = this.toStr(this.ret.recipient.name).trim();
        const rs = this.toStr(this.ret.recipient.staff_id).trim();
        if (rn) recipient = { name: rn, staff_id: rs ? rs : null };
      } else {
        const rv = this.toStr(this.ret.recipient).trim();
        if (rv) recipient = { name: rv, staff_id: null };
      }

      let productId = "";
      let productCode = "";
      if (this.ret.product && typeof this.ret.product === "object") {
        productId = this.toStr(this.ret.product.product_id).trim();
        productCode = this.toStr(this.ret.product.name).trim();
      }

      const payload = {
        requested_by: this.toStr(requestedBy),
        agent_id: this.toStr(agentId),
        customer_id: this.toStr(customerId),
        customer_name: customerName,
        recipient: recipient,
        item_serial_number: this.toStr(this.ret.item_serial_number),
        product_id: this.toStr(productId),
        product_code: this.toStr(productCode),
        product_name: this.toStr(this.ret.product_name),
        invoice_warranty_number: this.toStr(this.ret.invoice_warranty_number),
        reason_for_return: this.toStr(this.ret.reason_for_return),
        notes: this.toStr(this.ret.notes),
        status: this.toStr(this.ret.status || "open"),
      };

      if (this.ret.status === "confirmed") {
        if (this.ret.confirmed_by && typeof this.ret.confirmed_by === "object") {
          const cn = this.toStr(this.ret.confirmed_by.name).trim();
          const cs = this.toStr(this.ret.confirmed_by.staff_id).trim();
          if (cn) payload.confirmed_by = { name: cn, staff_id: cs ? cs : null };
        }
      }

      return payload;
    },
    async saveReturn() {
      try {
        if (!this.validateReturn()) return;

        this.isBusy = true;
        const payload = this.mapUiToApiPayload();

        if (this.returnId) {
          await this.ReturnService.updateReturn(this.returnId, payload);
          this.notifySuccess("Return updated successfully");
        } else {
          if (this.linkedTicketId) payload.ticket_id = this.linkedTicketId;
          await this.ReturnService.createReturn(payload);
          this.notifySuccess("Return created successfully");
        }

        await this.$router.push({ name: "returns" });
      } catch (error) {
        this.notifyError("Failed to save return");
      } finally {
        this.isBusy = false;
      }
    },
  },
};
</script>
